import {
  cartFoodProductDetails,
  getUpdateFoodOrder,
  proceedToCheckout,
  removeItemByGroupId,
  updateFoodProductToCart,
  updateLocationAndTimeForOrdering,
} from '@/shared/api/cart';
import { setLoading } from '@/shared/services/loading';
import { NotificationService } from '@/shared/services/notification';
import { TCartInitialData } from '@/shared/types/cart';
import { BasketPreiewAction } from '@/shared/utils/basketPreiew';
import { handleAvailableDates } from '@/shared/utils/handleAvailableDates';

import { voiceBasketErrorMessages } from '../utils';
import { setMain, setModel } from './slice';
import type {
  TActionFoodProductDetails,
  TActionGetPickUPTimesForCart,
  TActionProceedToCheckout,
  TActionRemoveItemByGroupId,
  TActionUpdateFoodProductToCart,
  TActionUpdateLocationAndTimeForOrdering,
} from './types';

export const loadCartData =
  (payload: TCartInitialData): CartAppThunk =>
  (dispatch) =>
    dispatch(setMain(payload));

export const GetPickUPTimesForCart: TActionGetPickUPTimesForCart =
  ({
    pickupTimeIncrement,
    date,
    locationId,
    storeIds,
    periodId,
    basketName,
    isModal,
    callback,
    handleTurnOffLoading,
  }) =>
  async (dispatch) => {
    if (!isModal) dispatch(setLoading(true));

    try {
      const response = await getUpdateFoodOrder({
        basketName,
        date,
        locationId,
        periodId,
        pickupTimeIncrement,
        storeIds,
      });

      const newMenuCalendar = handleAvailableDates(response.MenuCalendar);

      // The new calendar is added to Modal only when opened.
      if (callback) callback(response, newMenuCalendar);
    } catch (error) {
      NotificationService.showErrorNotifications((error as Error).message);
    } finally {
      if (!isModal) dispatch(setLoading(false));
      else handleTurnOffLoading?.();
    }
  };

export const RemoveItemByGroupId: TActionRemoveItemByGroupId =
  (groupId, basketName, callback) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await removeItemByGroupId({
        basketName,
        groupId,
      });

      dispatch(setModel(response.Cart));
      NotificationService.showErrorNotifications(response.Errors);

      if (callback) callback();
    } catch (error) {
      NotificationService.showErrorNotifications((error as Error).message);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const FoodProductDetails: TActionFoodProductDetails =
  ({ basket, prod, item }, callback) =>
  async (dispatch) => {
    dispatch(setLoading(true));

    try {
      const response = await cartFoodProductDetails({ basket, item, prod });

      if (response && callback) callback(response);

      // eslint-disable-next-line no-self-assign
      if (!response) window.location.href = window.location.href;
    } catch (error) {
      NotificationService.showErrorNotifications((error as Error).message);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const ProceedToCheckout: TActionProceedToCheckout = (basketName) => async (dispatch) => {
  dispatch(setLoading(true));

  try {
    const response = await proceedToCheckout(basketName);
    const { CheckoutLink, Errors, Cart } = response;
    if ((Errors?.length || !CheckoutLink) && Cart) {
      dispatch(setModel(Cart));
    }

    if (Errors?.length) return NotificationService.showErrorNotifications(Errors);

    if (!CheckoutLink) {
      const basket = Cart.Baskets.find(({ Name }) => Name === basketName);
      if (basket?.Errors.length) return voiceBasketErrorMessages(basket.Errors);

      NotificationService.showErrorNotifications('Unable to proceed to checkout');
    } else {
      window.location.assign(CheckoutLink);
    }
  } catch (error) {
    NotificationService.showErrorNotifications((error as Error).message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const UpdateFoodProductToCart: TActionUpdateFoodProductToCart =
  (
    { item, prod, basket, Modifiers, root, SelectedProductQuantity, RecipientInstructions },
    callback,
  ) =>
  async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await updateFoodProductToCart({
        item,
        prod,
        basket,
        Modifiers,
        root,
        SelectedProductQuantity,
        RecipientInstructions,
      });

      const MODAL_ERROR_CODE = '268';

      if (!response) {
        // eslint-disable-next-line no-self-assign
        window.location.href = window.location.href;
        return;
      }

      if (response.Cart) dispatch(setModel(response.Cart));

      dispatch(setLoading(false)); // scroll animation hack, disable loading animation before scrolling

      const currentBusket = response.Cart.Baskets.find(({ Name }) => Name === basket);
      if (currentBusket?.Errors) voiceBasketErrorMessages(currentBusket.Errors);

      const errors = response.Errors;
      if (errors.length) {
        const errorForShowInmodal = errors.find((errorItem) => errorItem.Code === MODAL_ERROR_CODE);
        const errorsForNotification = errors.reduce<string[]>((acc, errorItem) => {
          if (errorItem.Code === MODAL_ERROR_CODE) return acc;

          return [...acc, errorItem.Message];
        }, []);

        NotificationService.showErrorNotifications(errorsForNotification);

        if (errorForShowInmodal) {
          callback(errorForShowInmodal.Message);
          return;
        }
      }

      callback();
      BasketPreiewAction();
    } catch (error) {
      NotificationService.showErrorNotifications((error as Error).message);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const UpdateLocationAndTimeForOrdering: TActionUpdateLocationAndTimeForOrdering =
  ({ basket, updatedDateTime, pickupLocation, selectedDate, fulfillmentMethod, callback }) =>
  async (dispatch) => {
    dispatch(setLoading(true));

    try {
      const response = await updateLocationAndTimeForOrdering({
        basket,
        updatedDateTime,
        pickupLocation,
        selectedDate,
        fulfillmentMethod,
      });

      dispatch(setModel(response.Cart));
      NotificationService.showErrorNotifications(response.Errors.map((error) => error.Message));
      const currentBusket = response.Cart.Baskets.find(({ Name }) => Name === basket);
      if (currentBusket?.Errors) voiceBasketErrorMessages(currentBusket.Errors);

      if (callback) callback();
    } catch (error) {
      NotificationService.showErrorNotifications((error as Error).message);
    } finally {
      dispatch(setLoading(false));
    }
  };
